import React from "react";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { StructureType } from "@/types/silae";
import { CircleCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

interface WizardFooterProps {
  currentStep: number;
  totalSteps: number;
  wizardStructure: StructureType;
  onPreviousStep: () => void;
  onNextStep: () => void;
  isNextDisabled: boolean;
  className?: string;
}

/**
 * Composant footer du wizard avec navigation
 */
export const WizardFooter: React.FC<WizardFooterProps> = ({
  currentStep,
  totalSteps,
  wizardStructure,
  onPreviousStep,
  onNextStep,
  isNextDisabled,
  className = "",
}) => {
  const { t } = useTranslation();

  const getStructureLabel = (structure: StructureType) => {
    switch (structure) {
      case "simple":
        return t("wizardFooter.structureLabels.simple");
      case "with_conditions":
        return t("wizardFooter.structureLabels.with_conditions");
      case "if_else":
        return t("wizardFooter.structureLabels.if_else");
      default:
        return "";
    }
  };

  const getButtonText = () => {
    if (currentStep === totalSteps) {
      return t("wizardFooter.finish");
    } else if (currentStep === totalSteps - 1) {
      return t("wizardFooter.generate");
    } else {
      return t("wizardFooter.next");
    }
  };

  const getButtonIcon = () => {
    if (currentStep === totalSteps) {
      return <CircleCheck className="h-4 w-4" />;
    } else if (currentStep === totalSteps - 1) {
      return <Sparkles className="h-4 w-4" />;
    } else {
      return <ChevronRight className="h-4 w-4" />;
    }
  };

  return (
    <div
      className={`flex items-center justify-between h-16 max-h-16 min-h-16 border-t border-border p-6   ${className}`}
    >
      {/* Bouton Précédent */}
      <Button
        variant="outline"
        onClick={onPreviousStep}
        disabled={currentStep === 1}
        className="gap-2"
      >
        <ChevronLeft className="h-4 w-4" />
        {t("wizardFooter.previous")}
      </Button>

      {/* Informations centrales */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {t("wizardFooter.stepInfo", {
            current: currentStep,
            total: totalSteps,
          })}
        </span>
        {wizardStructure && (
          <>
            <span className="text-muted-foreground">•</span>
            <Badge variant="outline">
              {getStructureLabel(wizardStructure)}
            </Badge>
          </>
        )}
      </div>

      {/* Bouton Suivant/Générer */}
      <Button onClick={onNextStep} disabled={isNextDisabled} className="gap-2">
        {getButtonText()}
        {getButtonIcon()}
      </Button>
    </div>
  );
};
